import { IAmplifyProvider } from "../hexagon/adapters/driven/providers/amplifyProvider";
import { IlocalStorageProvider } from "../hexagon/adapters/driven/providers/localStorageProvider";
import { IAuthController } from "../hexagon/adapters/driver/authController";
import { useAppDispatch, useAppSelector } from "./redux";
import { setAuthState } from "../redux/Auth/auth.slice";
import {
  setClinicFloorList,
  setSelectedFloors,
  setUserClinic,
  setClinicState,
  setRedirectToUserInfo,
} from "../redux/Clinic/clinic.slice";
import { setLayoutState } from "../redux/Layout/layout.slice";
import { setNotificationSettings } from "../redux/Setting/setting.slice";
import { updateWatchlist } from "../redux/Watchlist/watchlist.slice";
import { featureFlag } from "../constants/feature";
import type { ITimeProvider } from "../hexagon/adapters/driven/providers/provider";
import { AppNotificationType } from "../constants/appNotification";
import type { IAppNotification } from "../types/app-notification";
import { setClinicalAlertFilter } from "../redux/Alert/alert.slice";
import { initialEscalatedAlertLevels } from "../components/CustomComponents/NotificationSettingsList/notification";
import { setDashboardFloor } from "../redux/Dashboard/dashboard.slice";
import { setSummaryState } from "../redux/Summary/summary.slice";
import type { ISummaryController } from "../hexagon/adapters/driver/summaryController";
import { setHomehealthState } from "../redux/Homehealth/homehealth.slice";
import { IHomeHealthController } from "../hexagon/adapters/driver/homeHealthController";
import { ISMSController } from "../hexagon/adapters/driver/smsController";
import { isHomeHealthExternalUser } from "../constants/sso";
import { getPatientPage } from "../constants/uiPaths";
import { isCompassusExperience } from "../util/isCompassusUser";
import { ONBOARDING_PENDING_KEY } from "../pages/Onboarding/onboardingKeys";
import { CIP_2_0_UI_FEATURE } from "../util/betaFeatures";
import { isCompassusMode } from "../util/use2026UI";

// Gather Health clinic. Users with access to this clinic are always allowed
// into CIP, even when their Cognito groups alone would otherwise restrict them.
const GATHER_HEALTH_CLINIC_ID = 11131;

// Latest clinic beta-feature request (login or facility switch). A response
// that arrives after a newer one started belongs to a clinic that is no longer
// selected and must not overwrite the current clinic's features.
let clinicBetaFeaturesRequestSeq = 0;

export const useRouterHook = ({
  amplifyProvider,
  authController,
  patientController,
  clinicController,
  localStorageProvider,
  notificationController,
  watchlistController,
  timeProvider,
  appNotification,
  circadiaEngineProvider,
  summaryController,
  homeHealthController,
  isPIN,
  smsController,
  userController,
}: {
  amplifyProvider: IAmplifyProvider;
  authController: IAuthController;
  clinicController: IClinicController;
  localStorageProvider: IlocalStorageProvider;
  notificationController: INotificationController;
  watchlistController: IWatchlistController;
  timeProvider: ITimeProvider;
  appNotification: IAppNotification;
  patientController: IPatientController;
  circadiaEngineProvider: ICircadiaEngineProvider;
  summaryController: ISummaryController;
  homeHealthController: IHomeHealthController;
  smsController: ISMSController;
  isPIN: () => boolean;
  userController: IUserController;
}) => {
  let isRedirectToUserInfo = false;
  let storedBiometricValue = null;
  const storedValue = localStorageProvider.get("isBiometric");
  if (storedValue !== null) {
    storedBiometricValue = storedValue === "true";
  }
  const dispatch = useAppDispatch();
  const { dashboardFloor, dotPhraseListResidentNote, persistedUser } =
    useAppSelector((state) => ({
      dashboardFloor: state.dashboard.dashboardFloor,
      clinicList: state.clinic?.clinicList || [],
      dotPhraseListResidentNote: state.summary.dotPhraseListResidentNote || [],
      persistedUser: state.auth.user,
    }));
  const populateAppData = async (changeFacility: boolean = false) => {
    amplifyProvider.amplifyConfigure();
    // Clinic beta features belong to the session's selected clinic: drop any
    // left over from a previous user/clinic (logout does not clear them) so
    // the 2026 UI gate fails closed until this session's clinic list loads.
    dispatch(
      setAuthState({
        clinicBetaFeaturesAllowedList: [],
        clinicBetaFeaturesLoaded: false,
      })
    );
    let clinicBetaFeaturesRequested = false;
    if (changeFacility) {
      localStorageProvider.remove("dashboard_filters");
    }
    dispatch(
      setLayoutState({
        layoutState: "loading",
      })
    );
    dispatch(setAuthState({ betaFeaturesLoaded: false }));

    // ── Phase 1: Fire all independent API calls in parallel ──
    const [
      dotPhraseResult,
      response,
      clinicResult,
      smsResult,
      userResult,
      hmacResult,
      betaResult,
    ] = await Promise.all([
      dotPhraseListResidentNote.length === 0 &&
      !isHomeHealthExternalUser(persistedUser?.groups, persistedUser?.email)
        ? summaryController.getDotPhrasesList({ ofType: "all" })
        : Promise.resolve(null),
      notificationController.getNotificationSetting(),
      clinicController.getMyClinics(),
      smsController.getPhoneNumber().catch(() => ({
        success: false,
        data: { phoneNumber: "", isVerified: false },
      })),
      authController.getCurrentUser(),
      isHomeHealthExternalUser(persistedUser?.groups, persistedUser?.email)
        ? Promise.resolve({ success: false, data: null })
        : patientController.getPatientHmac({
            platform: circadiaEngineProvider.plat,
          }),
      userController.getUserBetaFeatures(),
    ]);

    // Dispatch Phase 1 results that have no downstream dependencies
    if (dotPhraseResult?.success && dotPhraseResult?.data) {
      dispatch(
        setSummaryState({
          dotPhraseListResidentNote: dotPhraseResult.data,
        })
      );
    }

    dispatch(
      setAuthState({
        smsData: {
          phoneNumber: smsResult.data?.phoneNumber || "",
          isVerified: smsResult.data?.isVerified || false,
        },
      })
    );

    const user = userResult.data?.user;
    const hmac = hmacResult.data?.data;
    const userEmailFromV4Api = response.data?.data?.email;

    // Update user email from /user/v4 API if not present in Cognito user
    if (user && userEmailFromV4Api && !user.email) {
      user.email = userEmailFromV4Api;
      console.warn(
        "[useRouterHook] Updated user.email from /user/v4 API:",
        userEmailFromV4Api
      );
      dispatch(
        setAuthState({
          user,
        })
      );
    }

    if (betaResult.success && betaResult.data?.data) {
      let betaFeatures = betaResult.data?.data || [];

      // For home_health_external users (or Microsoft SSO users), force-enable third_party_scribe and heidi
      const isHomeHealthExternal = isHomeHealthExternalUser(
        user?.groups,
        user?.email
      );
      if (isHomeHealthExternal) {
        const forcedFeatures = ["third_party_scribe", "heidi"];
        betaFeatures = [...new Set([...betaFeatures, ...forcedFeatures])];
      }

      if (process.env.NODE_ENV === "development") {
        betaFeatures = [...new Set([...betaFeatures, "medrec"])];
      }

      dispatch(
        setAuthState({
          betaFeaturesAllowedList: betaFeatures,
          betaFeaturesLoaded: true,
        })
      );
    } else {
      dispatch(setAuthState({ betaFeaturesLoaded: true }));
    }

    // ── Phase 2: Select clinic (needs notification + clinic results) ──
    let getFacilityIdFromNotificationSetting = null;
    if (response.success) {
      getFacilityIdFromNotificationSetting =
        response.data?.data?.notification_preference
          ?.facilities_to_send_notification_for_old_id?.[0];
    }
    const { success: clinicSuccess, data: clinicData } = clinicResult;

    if (clinicSuccess && clinicData) {
      // First-time PCC SSO users are routed through the NEW onboarding (handled
      // by the onboarding gate in appRouter) instead of the legacy /user-info
      // screen. We flag them as an onboarding candidate here; the flag is
      // cleared once their onboarding reaches "completed".
      const isSSOUser = localStorageProvider.get("sso") === "true";
      const userInfoUpdatedEarly = localStorageProvider.get("userInfoUpdated");
      const hasProfileInDBEarly = !!(
        response.data?.data?.first_name && response.data?.data?.last_name
      );
      // Only users who will actually SEE the new onboarding skip /user-info:
      // it ships with the 2026 UI, so it needs the user's `cip2.0-ui` feature
      // and a clinic (the gate also needs the clinic's feature, which loads in
      // Phase 3 — see the fallback there). Everyone else keeps the legacy
      // /user-info setup.
      const userHasCip20Ui =
        betaResult.success &&
        (betaResult.data?.data ?? []).includes(CIP_2_0_UI_FEATURE);
      const isPccFirstTime =
        isSSOUser &&
        !!user?.pccOrgUuid &&
        !hasProfileInDBEarly &&
        !userInfoUpdatedEarly &&
        userHasCip20Ui &&
        clinicData.length > 0;
      if (isPccFirstTime && user?.email) {
        localStorageProvider.set(
          ONBOARDING_PENDING_KEY,
          user.email.trim().toLowerCase()
        );
      }

      if (clinicData.length === 0) {
        isRedirectToUserInfo = true;
        dispatch(setRedirectToUserInfo(true));
      }
      dispatch(
        setClinicState({
          clinicState: "loading",
          clinicList: clinicData.map((item) => {
            return item;
          }),
        })
      );
      let selectedClinic = null;
      if (
        getFacilityIdFromNotificationSetting &&
        response?.data?.data?.notification_preference
      ) {
        selectedClinic =
          clinicData.find(
            (item) => item.id === Number(getFacilityIdFromNotificationSetting)
          ) || clinicData[0];
        selectedClinic = await clinicController.setSelectedClinic({
          id: selectedClinic?.id || 0,
          clinicList: clinicData,
          notificationSettings: {
            email: response?.data?.data?.notification_preference?.email,
            pushNotification:
              response?.data?.data?.notification_preference?.push_notification,
            kioskMode:
              response?.data?.data?.notification_preference?.kiosk_mode,
            escalatedAlertPreference:
              response?.data?.data?.notification_preference
                ?.escalated_alert_preference,
            facilitiesToSendNotificationForOldId: [selectedClinic?.id || 0],
            psnAlertPreference:
              response?.data?.data?.notification_preference
                ?.psn_alert_preference,
            bedExitPreference:
              response?.data?.data?.notification_preference
                ?.bed_exit_preference,
            sms: response?.data?.data?.notification_preference?.sms,
            smsPreferences:
              response?.data?.data?.notification_preference?.sms_preferences,
          },
        });
      }
      if (!selectedClinic) {
        selectedClinic = clinicController.setDefaultClinic({
          clinicList: clinicData,
        });
      }

      // Profile redirect check (uses Phase 1 getCurrentUser + notifResponse)
      const skipClinicFloorPref = isCompassusExperience({
        user: userResult.data?.user,
        userClinic: selectedClinic,
      });
      const isSSO = localStorageProvider.get("sso") === "true";
      const userInfoUpdatedFlag = localStorageProvider.get("userInfoUpdated");
      let shouldRedirectToUserInfo = false;

      if (isSSO) {
        if (userInfoUpdatedFlag) {
          shouldRedirectToUserInfo = false;
        } else {
          const hasProfileInDB =
            response.data?.data?.first_name && response.data?.data?.last_name;
          shouldRedirectToUserInfo = !hasProfileInDB;
        }
      } else {
        shouldRedirectToUserInfo =
          !userResult.data?.user?.fullName && !userInfoUpdatedFlag;
      }

      if (
        !isRedirectToUserInfo &&
        shouldRedirectToUserInfo &&
        !isPccFirstTime
      ) {
        isRedirectToUserInfo = true;
        dispatch(setRedirectToUserInfo(true));
      }

      if (selectedClinic) {
        timeProvider.setTimezone(selectedClinic.timezone || "");
        // `selectedClinic` is a row from my-clinic-slim (`getMyClinics`); the API includes
        // `config` on each clinic when present—no second config fetch.
        dispatch(setUserClinic({ clinic: selectedClinic }));

        // ── Phase 3: Parallel calls that all need selectedClinic ──
        const homeHealthPromise = (async () => {
          let patientId: number | null = null;
          if (
            selectedClinic?.is_home_health_enabled &&
            !userResult.data?.user?.homeHealthId
          ) {
            const res =
              await homeHealthController.getSelectedFacilityPatientList({
                clinic_id: selectedClinic.id,
              });
            const { success: homeHealthSuccess, data: homeHealthData } = res;
            if (homeHealthSuccess && homeHealthData) {
              const patientList = homeHealthData as unknown as IHHPatient[];
              const c100DeviceIdDictionary = patientList.reduce(
                (acc: Dictionary<IHHPatient>, item: IHHPatient) => {
                  const c100DeviceId = item.devices?.find(
                    (device: any) => device.device_type === "c100"
                  );
                  if (c100DeviceId) {
                    acc[c100DeviceId.device_id] = item;
                  }
                  return acc;
                },
                {}
              );
              dispatch(
                setHomehealthState({
                  patients: patientList,
                  c100DeviceIdDictionary,
                })
              );
            }
          } else if (
            selectedClinic?.is_home_health_enabled &&
            userResult.data?.user?.homeHealthId
          ) {
            const res = await homeHealthController.getSinglePatientInfo({
              patient_id: userResult.data?.user?.homeHealthId,
            });
            if (res.success && res.data) {
              const c100DeviceIdDictionary: Dictionary<IHHPatient> = {};
              res.data.devices.forEach((item) => {
                if (item.device_type === "c100") {
                  item._id = res.data?._id;
                  c100DeviceIdDictionary[item.device_id] = item;
                  patientId = item.device_id;
                }
              });
              dispatch(
                setHomehealthState({
                  patients: [
                    {
                      ...res.data,
                      patient_id: patientId,
                    } as unknown as IHHPatient,
                  ],
                  c100DeviceIdDictionary,
                })
              );
              // Home-health users are locked to the mobile profile route by
              // HomeHealthRouteGuard, so never use the desktop /patient path.
              if (patientId) {
                window.location.replace(`/#${getPatientPage(patientId)}`);
              }
            }
          }
        })();

        const notificationSettingsPromise = (async () => {
          if (!featureFlag.notificationSettings) return null;
          const isNewUser = localStorageProvider.get("isNewUser");
          if (!response.data) return null;

          let notificationRes = null;
          if (isNewUser) {
            const defaultConfig = {
              email: false,
              pushNotification: true,
              kioskMode: false,
              escalatedAlertPreference: initialEscalatedAlertLevels.map(
                (item) => item.value
              ),
              facilitiesToSendNotificationForOldId: [selectedClinic.id],
              psnAlertPreference: ["PsnLevel3Acute", "PsnLevel3"],
              bedExitPreference: [],
              sms: false,
              smsPreferences: {
                morning_report: false,
                afternoon_report: false,
                evening_report: false,
                just_in_time: false,
              },
            };
            notificationRes =
              await notificationController.setNotificationSetting(
                defaultConfig
              );
            dispatch(setNotificationSettings(defaultConfig));
            localStorageProvider.remove("isNewUser");
          } else {
            notificationRes =
              await notificationController.setNotificationSetting({
                email: response.data.data?.notification_preference?.email,
                pushNotification:
                  response.data.data?.notification_preference
                    ?.push_notification !== false,
                kioskMode:
                  response.data.data?.notification_preference?.kiosk_mode,
                escalatedAlertPreference:
                  response.data.data?.notification_preference
                    ?.escalated_alert_preference,
                facilitiesToSendNotificationForOldId: [selectedClinic.id],
                psnAlertPreference:
                  response.data.data?.notification_preference
                    ?.psn_alert_preference,
                bedExitPreference:
                  response.data.data?.notification_preference
                    ?.bed_exit_preference,
                sms: response.data.data?.notification_preference?.sms,
                smsPreferences:
                  response.data.data?.notification_preference?.sms_preferences,
              });

            const autoBedExitPrefs = (
              response.data.data?.notification_preference as any
            )?.auto_bed_exit_preference as
              | Array<{
                  clinic_id: number;
                  is_enabled: boolean;
                }>
              | undefined;
            const autoBedExit72h =
              autoBedExitPrefs?.find(
                (pref) => pref.clinic_id === selectedClinic?.id
              )?.is_enabled || false;

            dispatch(
              setNotificationSettings({
                email: response.data.data?.notification_preference?.email,
                pushNotification:
                  response.data.data?.notification_preference
                    ?.push_notification !== false,
                kioskMode:
                  response.data.data?.notification_preference?.kiosk_mode,
                escalatedAlertPreference:
                  response.data.data?.notification_preference
                    ?.escalated_alert_preference,
                facilitiesToSendNotificationForOldId: [selectedClinic.id],
                psnAlertPreference:
                  response.data.data?.notification_preference
                    ?.psn_alert_preference,
                bedExitPreference:
                  response.data.data?.notification_preference
                    ?.bed_exit_preference,
                sms: response.data.data?.notification_preference?.sms,
                smsPreferences:
                  response.data.data?.notification_preference?.sms_preferences,
                autoBedExit72h,
              })
            );
          }
          return notificationRes;
        })();

        const floorPromise = skipClinicFloorPref
          ? Promise.resolve(null)
          : clinicController.getClinicFloor({
              clinicId: selectedClinic.id,
            });

        // Clinic-scoped beta features for the selected clinic. Fetched with the
        // clinic id so the response includes `clinicFeatures`; stored separately
        // from the (user-scoped) `betaFeaturesAllowedList` so gates like the
        // 2026 UI can require BOTH the user and the clinic to be enrolled.
        clinicBetaFeaturesRequested = true;
        const clinicBetaFeaturesPromise = (async () => {
          const requestSeq = ++clinicBetaFeaturesRequestSeq;
          // Fail closed while loading: the previously selected clinic's
          // features must not gate (e.g. show the 2026 UI for) this clinic.
          dispatch(setAuthState({ clinicBetaFeaturesAllowedList: [] }));
          const clinicBetaResult = await userController.getUserBetaFeatures({
            clinicId: selectedClinic.id,
          });
          // A newer clinic selection started meanwhile: its result wins.
          if (requestSeq !== clinicBetaFeaturesRequestSeq) return;
          const clinicFeatures = clinicBetaResult.success
            ? clinicBetaResult.data?.clinicFeatures ?? []
            : [];
          dispatch(
            setAuthState({
              clinicBetaFeaturesAllowedList: clinicFeatures,
              clinicBetaFeaturesLoaded: true,
            })
          );
          // A first-time PCC user skipped /user-info expecting the new
          // onboarding, but it only runs when the selected clinic is on the
          // 2026 UI too. If not, fall back to the legacy setup screen.
          const clinicOn2026UI =
            clinicFeatures.includes(CIP_2_0_UI_FEATURE) &&
            !isCompassusMode({
              clinicType: selectedClinic.clinic_type,
              isHomeHealthRPM: selectedClinic.config?.isHomeHealthRPM,
            });
          if (
            isPccFirstTime &&
            shouldRedirectToUserInfo &&
            !isRedirectToUserInfo &&
            !clinicOn2026UI
          ) {
            isRedirectToUserInfo = true;
            dispatch(setRedirectToUserInfo(true));
          }
        })();

        const [, notificationSaveResult, floorResult] = await Promise.all([
          homeHealthPromise,
          notificationSettingsPromise,
          floorPromise,
          clinicBetaFeaturesPromise,
        ]);

        // Preserve early-return on notification save error
        if (notificationSaveResult?.error) {
          appNotification({
            nTitle: notificationSaveResult.error.message,
            nType: AppNotificationType.ERROR,
          });
          return;
        }
        if (notificationSaveResult?.data) {
          dispatch(
            setAuthState({
              userPreference: notificationSaveResult.data
                .data as unknown as UserPreference,
            })
          );
        }

        // ── Phase 4: Process floors, then parallel watchlist + summary ──
        if (skipClinicFloorPref) {
          dispatch(setClinicFloorList({ clinicFloorList: [] }));
          dispatch(setSelectedFloors([]));
          dispatch(setDashboardFloor([]));
          dispatch(setClinicalAlertFilter({ floors: [] }));
        } else {
          const { success: floorSuccess, data: floorData } = floorResult ?? {};

          if (floorSuccess && floorData) {
            dispatch(setClinicFloorList({ clinicFloorList: floorData }));
            const selectedFloors: number[] = [];
            floorData.forEach((item) => {
              if (item.enabled) selectedFloors.push(item.floor_id);
            });
            const allClinicFloors = floorData.map((item) => item.floor_id);
            let selectFloors = selectedFloors;
            if (floorData.length > 0 && selectedFloors.length === 0) {
              const Allfloors = await clinicController.setClinicFloor({
                clinicId: selectedClinic.id,
                floor_ids: floorData.map((item) => item.floor_id),
              });
              selectFloors = Allfloors.data?.floors || [];
              dispatch(
                setClinicFloorList({
                  clinicFloorList: floorData.map((item) => ({
                    ...item,
                    enabled: true,
                  })),
                })
              );
            }
            dispatch(setSelectedFloors(selectFloors));
            if (
              changeFacility ||
              selectFloors.length >= dashboardFloor.length
            ) {
              dispatch(setDashboardFloor(selectFloors));
              dispatch(
                setClinicalAlertFilter({
                  floors: selectFloors.map((item) => Number(item)),
                })
              );
            }

            // Watchlist + Summary in parallel
            const [watchlistResult, summaryResult] = await Promise.all([
              watchlistController.getWatchlist({
                clinicId: selectedClinic.id,
                floorIds: allClinicFloors,
              }),
              selectFloors && selectFloors.length > 0
                ? summaryController.getSummary({
                    clinicId: selectedClinic.id,
                    floorIds: selectFloors,
                    timezone: selectedClinic.timezone || "",
                  })
                : Promise.resolve(null),
            ]);

            if (watchlistResult.success && watchlistResult.data) {
              dispatch(
                updateWatchlist({
                  watchlist: watchlistResult.data.data || [],
                })
              );
            }
            if (summaryResult?.success && summaryResult?.data) {
              dispatch(setSummaryState({ summary: summaryResult.data }));
            }
          }
        }
      }
      dispatch(setClinicState({ clinicState: "pending" }));
    }

    // Intercom login (reuses Phase 1 getCurrentUser + getPatientHmac results).
    // Compassus experiences only expose Intercom through their Settings entry.
    // No clinic selected this session: there are no clinic features to wait for.
    if (!clinicBetaFeaturesRequested) {
      dispatch(setAuthState({ clinicBetaFeaturesLoaded: true }));
    }

    const selectedClinicForIntercom = clinicData?.length
      ? clinicController.getSelectedClinic({ clinicList: clinicData })
      : null;
    if (
      user &&
      hmac &&
      clinicData?.length &&
      !isCompassusExperience({
        user,
        userClinic: selectedClinicForIntercom,
      })
    ) {
      if (
        selectedClinicForIntercom &&
        selectedClinicForIntercom?.name &&
        user.fullName
      ) {
        const token = await authController.getAuthenticationToken();
        circadiaEngineProvider.loginIntercomUser(token, hmac, {
          name: user.fullName,
          user_id: user.userID,
          email: user.email,
          facility: selectedClinicForIntercom?.name,
        });
      } else if (user.userID) {
        const token = await authController.getAuthenticationToken();
        circadiaEngineProvider.loginIntercomUser(token, hmac, {
          name: user.fullName || "",
          user_id: user.userID,
          email: user.email || "",
          facility: selectedClinicForIntercom?.name || "",
        });
      }
    }

    dispatch(
      setLayoutState({
        layoutState: "pending",
      })
    );
  };

  const getAuthState = async () => {
    dispatch(
      setAuthState({
        authState: "loading",
      })
    );

    // Z-Pax Auth Integration will be handled by useZpaxAuth hook in components
    // The hook handles detection, callback parsing, and token exchange
    // Normal auth flow continues here

    const { data, error } = await authController.getCurrentUser();
    if (error && error === "pinRequired") {
      dispatch(
        setAuthState({
          isAuthorized: true,
          authState: "pending",
        })
      );
      return;
    }

    if (!data || !data.user) {
      dispatch(
        setAuthState({
          authState: "pending",
          isAuthorized: false,
          isPinAuthorized: false,
        })
      );
      return "notAuthorized";
    }
    const isCredentials = localStorageProvider.get("internalUse");
    const isBiometric = localStorageProvider.get("isBiometric");
    if (data && data.user && isPIN() ? isCredentials : true) {
      localStorageProvider.set(
        "username",
        `${data.user?.firstName} ${data.user?.lastName || ""}`
      );

      const groups = data.user.groups || [];
      const hasOnlyPatientGroups =
        groups.every((g) =>
          (["home_health_patient", "member"] as string[]).includes(g)
        ) && groups.includes("member" as UserGroup);
      // Home-health patients provisioned only into the app-setup group (i.e.
      // exactly `home_health_patient` + `setup_app_user` and nothing else) are
      // not permitted into CIP — send them to the restricted page.
      const hasOnlySetupHomeHealthGroups =
        groups.length > 0 &&
        groups.every((g) =>
          (["home_health_patient", "setup_app_user"] as string[]).includes(g)
        ) &&
        groups.includes("home_health_patient" as UserGroup) &&
        groups.includes("setup_app_user" as UserGroup);
      // Home-health patients whose only Cognito group is `home_health_patient`
      // (i.e. member-app patient accounts with no clinician/staff groups) are
      // not permitted into CIP either.
      const hasOnlyHomeHealthPatientGroup =
        groups.length === 1 && groups[0] === "home_health_patient";
      let hasNoAccess =
        groups.length < 1 ||
        hasOnlyPatientGroups ||
        hasOnlySetupHomeHealthGroups ||
        hasOnlyHomeHealthPatientGroup;

      // Internal override: Circadia staff (any @circadia.health email) are
      // always allowed in, regardless of their Cognito groups.
      if (
        hasNoAccess &&
        data.user.email?.toLowerCase().endsWith("@circadia.health")
      ) {
        hasNoAccess = false;
      }

      // Clinic-based override: a user whose Cognito groups would otherwise
      // restrict them is still allowed in if they have access to the Gather
      // Health clinic AND they have `setup_app_user`. Gather Health requested
      // CIP access only for users with setup-app access. Only look this up
      // when we'd otherwise block them, so the normal (allowed) path stays a
      // single round-trip.
      if (hasNoAccess && groups.includes("setup_app_user" as UserGroup)) {
        try {
          const clinicsRes = await clinicController.getMyClinics();
          const hasGatherHealthClinic =
            clinicsRes.success &&
            (clinicsRes.data || []).some(
              (clinic) => clinic.id === GATHER_HEALTH_CLINIC_ID
            );
          if (hasGatherHealthClinic) {
            hasNoAccess = false;
          }
        } catch {
          // If the clinic lookup fails, fall back to the group-based decision.
        }
      }

      if (isBiometric && isBiometric === "true") {
        dispatch(
          setAuthState({
            user: data.user,
            isAuthorized: true,
            isPinAuthorized: true,
            hasNoAccess,
            authState: "pending",
          })
        );
        return "biometric";
      } else {
        dispatch(
          setAuthState({
            user: data.user,
            isAuthorized: true,
            hasNoAccess,
            authState: "pending",
          })
        );
      }
      return;
    }

    dispatch(
      setAuthState({
        authState: "pending",
      })
    );
  };

  return {
    populateAppData,
    getAuthState,
    storedBiometricValue,
  };
};
